import { useState, useCallback, useMemo, useEffect, useRef, type HTMLAttributes } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Play } from 'lucide-react'
import {
  useCreateProject,
  useDeleteProject,
  useDuplicateProject,
  useStarProject,
  useUnstarProject,
  useReorderTabs,
  useDashboardTabs,
  useDashboardTabProjects,
  useAssignProject,
  useUnassignProject,
  useReorderProjects,
  useUpdateProject,
  DASHBOARD_PAGE_SIZE,
} from '../api/hooks'
import { toast } from 'sonner'
import Button from '../components/ui/Button'
import { getThumbnailUrl } from '../api/useAssetUrl'
import DashboardSearchInput from '../components/dashboard/DashboardSearchInput'
import DashboardTabBar from '../components/dashboard/DashboardTabBar'
import MoveToTeamDialog from '../components/dashboard/MoveToTeamDialog'
import { isProjectListLoading as computeProjectListLoading } from '../components/dashboard/projectListLoading'
import { shouldRedirectToGuidedCreation } from '../components/dashboard/guidedCreationRedirect'
import { useActiveTeam } from '../contexts/ActiveTeamContext'
import { canDeleteTeamProject } from '../features/teams/permissions'
import VisibilitySwitch, {
  asVisibility,
  DEFAULT_VISIBILITY,
  type ProjectVisibility,
} from '../components/dashboard/VisibilitySwitch'
import Pagination from '../components/dashboard/Pagination'
import { useAuth } from '../contexts/AuthContext'
import { usePublishProjectToTeam } from '../hooks/useTeams'
import type { UserAppMetadata } from '../lib/gotrueTypes'
import { APP_NAME } from '../constants/brand'
import { DEFAULT_STYLE } from '../constants/styles'
import {
  DndContext,
  closestCenter,
  pointerWithin,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type CollisionDetection,
  DragOverlay,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type {
  ProjectNodePreview,
  ProjectListItem,
  ProjectStatus,
  TabSortBy,
  TabSortDirection,
} from '../api/types'
import {
  ProjectStatusSelect,
  projectStatusBadgeClass,
  projectStatusDotClass,
} from '../components/ui/ProjectStatusSelect'
import { createDebug, createNadDebug } from '../utils/debugLog'
import { useTranslation } from 'react-i18next'
import { normalizeLocale } from '../i18n/supportedLocales'
import { projectPreviewCandidates } from '../utils/projectPreviews'
import { trackInteraction, resultOf } from '../analytics/interactionAnalytics'
import { teamContextFor } from '../analytics/teamContext'
import { newAnalyticsId } from '../analytics/chatHeaderAnalytics'

const dashboardLog = createDebug('dw:dashboard-tabs')
const dashboardAvailabilityLog = createNadDebug('dw:dashboard-tabs:availability')
const newProjectLog = createNadDebug('dw:dashboard:new-project')
const duplicateProjectLog = createNadDebug('dw:dashboard:duplicate-project')
// `dw:` prefix is required: debugLog enables `dw:*` only, and _parseNamespace
// strips it so the emitted `feature` is `project-status-lifecycle` — the same
// tag the backend uses, so one `nad trace --feature` call shows both sides.
const statusLog = createNadDebug('dw:project-status-lifecycle')
const reorderLog = createNadDebug('dw:dashboard-tabs:reorder')
// PII guard: only the length + tab id are logged, never the literal query
// text. The frontend tag mirrors the backend slog field `name_prefix` on the
// same `dashboard-tabs` feature. Search is a case-insensitive SUBSTRING match;
// the channel name keeps the historical `name-prefix-search` tag for continuity.
const dashboardSearchLog = createDebug('dw:dashboard-tabs:name-prefix-search')
const SEARCH_DEBOUNCE_MS = 500
const MAX_CHIPS = 3
const ALL_TAB_ID = 'all'

function createTraceId(): string {
  try {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return globalThis.crypto.randomUUID().replace(/-/g, '').slice(0, 12)
    }
  } catch {
    // Logging must never prevent the navigation it describes.
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`.slice(0, 12)
}

const DEV_REPLAY_WORKFLOW_PROMPT = [
  'Developer replay E2E. Create a short deterministic video editor preview with one video clip and one audio track.',
  'Use the configured replay fixture and mocked assets. Do not call real model or media providers.',
].join(' ')
const DEV_REPLAY_WORKFLOW_SCENARIO = 'dashboard-video-editor'

type TrustedAppMetadata = UserAppMetadata & { role?: string }

function canRunDeveloperWorkflow(user: { app_metadata?: TrustedAppMetadata } | null | undefined): boolean {
  const isLocalDev =
    import.meta.env.DEV &&
    !import.meta.env.VITE_ENABLE_AUTH &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  return isLocalDev || user?.app_metadata?.role === 'admin'
}

function PreviewChips({
  projectId,
  previews,
}: {
  projectId: string
  previews: ProjectNodePreview[]
}) {
  const [hidden, setHidden] = useState<Set<number>>(new Set())
  const onError = useCallback((idx: number) => {
    setHidden((prev) => new Set(prev).add(idx))
  }, [])

  const visible = previews.filter((_, i) => !hidden.has(i))
  if (visible.length === 0) return null

  const shown = visible.slice(0, MAX_CHIPS)

  return (
    <div className="hidden md:flex items-center gap-2 flex-shrink-0" data-testid="preview-chips">
      {shown.map((p) => {
        const url = getThumbnailUrl(projectId, p.image_path, 240)
        if (!url) return null
        return (
          <img
            key={p.image_path}
            src={url}
            alt=""
            onError={() => onError(previews.indexOf(p))}
            className="w-[120px] h-[80px] rounded-lg object-cover border border-border"
          />
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Move to Team (RFC-1371 D3) — ONE WAY
// ---------------------------------------------------------------------------

/**
 * Opens a confirmation dialog; the dialog performs the move.
 *
 * Previously this was a two-click arm/fire button. A modal is the better fit
 * for D3: publishing is irreversible (there is no unpublish route,
 * deliberately), and a second click on the same control communicates nothing
 * about WHY it is guarded. The dialog can say "this cannot be undone" in words
 * and offers an explicit way out, which an arming button never does — backing
 * out of it means noticing the tooltip changed and then waiting three seconds.
 *
 * Rendered only when the server said `can_publish`, computed by the same
 * predicate the publish route enforces — never a client-side copy of the rule.
 */
function MoveToTeamButton({
  projectId,
  onRequestMove,
  isMoving,
}: {
  projectId: string
  onRequestMove: () => void
  isMoving?: boolean
}) {
  const { t } = useTranslation('pages')
  return (
    <button
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        if (isMoving) return
        onRequestMove()
      }}
      disabled={isMoving}
      className="p-2.5 border border-transparent hover:bg-primary/10 hover:border-primary/20 rounded-lg transition-all cursor-pointer flex-shrink-0 disabled:cursor-wait disabled:opacity-70"
      title={t('home.moveToTeam')}
      data-testid={`project-move-to-team-${projectId}`}
    >
      {isMoving ? (
        <svg className="w-4 h-4 text-primary animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : (
        <svg
          className="w-4 h-4 text-white/30 group-hover:text-primary transition-colors"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )}
    </button>
  )
}

// Project card asset counts: 2 rows x 3 columns
// ---------------------------------------------------------------------------

type ProjectCountKey = 'character' | 'location' | 'object' | 'video' | 'audio' | 'image'

const PROJECT_COUNT_CELLS: readonly { key: ProjectCountKey; count: (p: ProjectListItem) => number }[] = [
  { key: 'character', count: (p: ProjectListItem) => p.character_count },
  { key: 'location', count: (p: ProjectListItem) => p.location_count },
  { key: 'object', count: (p: ProjectListItem) => p.object_count ?? 0 },
  { key: 'video', count: (p: ProjectListItem) => p.video_count ?? 0 },
  { key: 'audio', count: (p: ProjectListItem) => p.audio_count ?? 0 },
  { key: 'image', count: (p: ProjectListItem) => p.image_count ?? 0 },
]

function ProjectCountGrid({ project }: { project: ProjectListItem }) {
  const { t } = useTranslation('pages')
  return (
    <div
      className="grid grid-cols-3 gap-x-6 gap-y-1 max-w-md"
      data-testid={`project-counts-${project.id}`}
    >
      {PROJECT_COUNT_CELLS.map(({ key, count }) => {
        const n = count(project)
        return (
          <span
            key={key}
            className="text-xs font-medium text-text-secondary whitespace-nowrap"
            data-testid={`project-count-${key}-${project.id}`}
          >
            <span className="font-semibold text-foreground tabular-nums mr-1.5">{n}</span>
            {t(`home.count.${key}`, { count: n })}
          </span>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Project card content (shared between SortableProjectCard and DragOverlay)
// ---------------------------------------------------------------------------

function ProjectCardContent({
  project,
  onDeleteClick,
  onDuplicateClick,
  isDuplicating,
  onMoveToTeamClick,
  isMovingToTeam,
  onStarClick,
  onStatusChange,
  isUpdatingStatus,
  dragHandleProps,
  isDragging,
}: {
  project: ProjectListItem
  onDeleteClick?: () => void
  onDuplicateClick?: () => void
  isDuplicating?: boolean
  onMoveToTeamClick?: () => void
  isMovingToTeam?: boolean
  onStarClick?: () => void
  onStatusChange?: (status: ProjectStatus) => void
  isUpdatingStatus?: boolean
  dragHandleProps?: HTMLAttributes<HTMLDivElement>
  isDragging?: boolean
}) {
  const { t, i18n } = useTranslation('pages')
  const isStarred = !!project.starred_at
  return (
    <div
      className={`group p-5 rounded-md bg-panel border border-border hover:border-primary/50 transition-all duration-200 ${isDragging ? 'opacity-0' : ''}`}
      data-testid={`project-card-${project.id}`}
    >
      <div className="flex items-center gap-4">
        {/* Drag handle */}
        <div
          {...(dragHandleProps ?? {})}
          className="cursor-grab active:cursor-grabbing p-1 -ml-2 opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity flex-shrink-0"
          title={t('home.dragToReorder')}
          data-testid={`project-drag-handle-${project.id}`}
        >
          <svg className="w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </div>

        <Link
          to={`/project/${project.id}/workspace`}
          className="flex-1 min-w-0 cursor-pointer"
          onClick={() => trackInteraction('dashboard_project_opened', { project_id: project.id })}
        >
          <div className="flex items-center gap-2 mb-3 min-w-0">
            {onStarClick && (
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onStarClick()
                }}
                className={`p-0.5 flex-shrink-0 transition-colors cursor-pointer ${isStarred ? 'text-yellow-400' : 'text-white/20 hover:text-yellow-400/70'}`}
                title={isStarred ? t('home.unstarProject') : t('home.starProject')}
                data-testid={`project-star-${project.id}`}
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill={isStarred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </button>
            )}
            <h3 className="text-lg font-semibold text-white truncate">
              {project.name}
            </h3>
          </div>
          <ProjectCountGrid project={project} />
          <div className="flex gap-x-6 gap-y-1 flex-wrap mt-3">
            <span className="text-xxs font-medium text-text-muted">
              {t('home.created', { date: new Date(project.created_at).toLocaleString(i18n.language) })}
            </span>
            <span className="text-xxs font-medium text-text-muted">
              {t('home.modified', { date: new Date(project.updated_at).toLocaleString(i18n.language) })}
            </span>
          </div>
        </Link>
        {(() => {
          const allPreviews = projectPreviewCandidates(project)
          return allPreviews.length > 0 ? (
            <PreviewChips projectId={project.id} previews={allPreviews} />
          ) : null
        })()}
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {onStatusChange ? (
            <ProjectStatusSelect
              projectId={project.id}
              value={project.status}
              onChange={onStatusChange}
              disabled={isUpdatingStatus}
            />
          ) : (
            // Drag overlay: a live dropdown inside the dnd-kit clone would be a
            // second control with the same test id, so render a read-only badge
            // that borrows the control's colour scheme.
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xxs font-medium
                uppercase tracking-wider border rounded-full ${projectStatusBadgeClass(project.status)}`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${projectStatusDotClass(project.status)}`}
                aria-hidden="true"
              />
              {t(`home.status.${project.status}`)}
            </span>
          )}
          <div className="flex items-center">
            {/* `can_publish` is server-computed; absent (teams off) it is false. */}
            {onMoveToTeamClick && project.can_publish && (
              <MoveToTeamButton
                projectId={project.id}
                onRequestMove={onMoveToTeamClick}
                isMoving={isMovingToTeam}
              />
            )}
            {onDuplicateClick && (
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (isDuplicating) return
                  onDuplicateClick()
                }}
                disabled={isDuplicating}
                className="p-2.5 hover:bg-primary/10 border border-transparent hover:border-primary/20 rounded-lg transition-all cursor-pointer flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-panel disabled:cursor-wait disabled:opacity-70"
                title={t('home.duplicateProject')}
                data-testid={`project-duplicate-${project.id}`}
              >
                {isDuplicating ? (
                  <svg className="w-4 h-4 text-primary animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-white/30 group-hover:text-primary transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h6a2 2 0 002-2v-2" />
                  </svg>
                )}
              </button>
            )}
            {onDeleteClick && (
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onDeleteClick()
                }}
                className="p-2.5 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 rounded-lg transition-all cursor-pointer flex-shrink-0"
                title={t('home.deleteProject')}
              >
                <svg className="w-4 h-4 text-white/30 group-hover:text-red-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sortable project card (draggable for tab assignment + reorder)
// ---------------------------------------------------------------------------

function SortableProjectCard({
  project,
  onDeleteClick,
  onDuplicateClick,
  isDuplicating,
  onMoveToTeamClick,
  isMovingToTeam,
  onStarClick,
  onStatusChange,
  isUpdatingStatus,
  dragDisabled = false,
}: {
  project: ProjectListItem
  // Optional: absent means the viewer may not delete this project (a
  // team-visible project they do not administer), so no button is drawn.
  onDeleteClick?: () => void
  onDuplicateClick: () => void
  isDuplicating: boolean
  onMoveToTeamClick?: () => void
  isMovingToTeam?: boolean
  onStarClick: () => void
  onStatusChange: (status: ProjectStatus) => void
  isUpdatingStatus: boolean
  /**
   * When true, the drag handle is hidden and dnd-kit listeners are detached.
   * Used while a search query is active — the reorder math depends on
   * `pageOffset` over the full unfiltered project_ids list, so allowing a
   * drag while filtered would silently corrupt the manual order.
   */
  dragDisabled?: boolean
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `project-${project.id}`,
    data: { type: 'project', projectId: project.id },
    disabled: dragDisabled,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <ProjectCardContent
        project={project}
        onDeleteClick={onDeleteClick}
        onDuplicateClick={onDuplicateClick}
        isDuplicating={isDuplicating}
        onMoveToTeamClick={onMoveToTeamClick}
        isMovingToTeam={isMovingToTeam}
        onStarClick={onStarClick}
        onStatusChange={onStatusChange}
        isUpdatingStatus={isUpdatingStatus}
        dragHandleProps={dragDisabled ? undefined : { ...attributes, ...listeners }}
        isDragging={isDragging}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Custom collision detection: prefer tab-drop targets when pointer is
// over them, otherwise fall back to closestCenter for project reordering
// and tab sorting.
// ---------------------------------------------------------------------------

const tabAwareCollision: CollisionDetection = (args) => {
  const activeType = args.active.data.current?.type

  // Project drags use dedicated hover overlays rendered above tabs.
  const pointerCollisions = pointerWithin(args)
  if (activeType === 'project') {
    const tabHit = pointerCollisions.find((c) => {
      const id = String(c.id)
      return id === 'project-hover-all' || id.startsWith('project-hover-tab-')
    })
    if (tabHit) return [tabHit]
  }

  // For tab-on-tab and project-on-project sorting, use closestCenter
  return closestCenter(args)
}

// ---------------------------------------------------------------------------
// URL state helpers
// ---------------------------------------------------------------------------

function parsePage(raw: string | null): number {
  if (!raw) return 1
  const n = parseInt(raw, 10)
  return Number.isFinite(n) && n >= 1 ? n : 1
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabIdParam = searchParams.get('tab') ?? ALL_TAB_ID
  // URL-backed like `tab` and `page`, so a reload or a shared link lands on the
  // same half. `asVisibility` narrows anything else to Private rather than
  // trusting the param — an unknown value must not widen the list.
  const visibility = asVisibility(searchParams.get('visibility'))
  const page = parsePage(searchParams.get('page'))
  // DashboardTabBar uses `null` for the All chip; URL uses 'all'.
  const activeTabId = tabIdParam === ALL_TAB_ID ? null : tabIdParam

  // The All-tab sort is a client-side preference; named tabs persist their
  // own sort via PATCH. Keeping it in component state — not the URL —
  // matches the prior behavior: refresh resets to the default sort.
  const [allSortBy, setAllSortBy] = useState<TabSortBy>('updated_at')
  const [allSortDirection, setAllSortDirection] = useState<TabSortDirection>('desc')

  // Prefix-search state. `?q=` is the persisted form (deep-link / refresh /
  // back-nav restoration). `inputValue` is the immediate input value used to
  // keep typing responsive; a 500 ms debounce writes `inputValue` back to
  // the URL. The lowercased trimmed value is what reaches the backend.
  const urlQuery = searchParams.get('q') ?? ''
  const [inputValue, setInputValue] = useState(urlQuery)
  // Restore from URL on browser navigation (back/forward) — only when the
  // user is NOT actively typing (the debounce window).
  useEffect(() => {
    setInputValue(urlQuery)
  }, [urlQuery])
  const trimmedQuery = urlQuery.trim()
  const isSearching = trimmedQuery !== ''
  const namePrefix = trimmedQuery ? trimmedQuery.toLowerCase() : undefined

  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null)
  const deleteProject = useDeleteProject()
  // Per-card in-flight set — `duplicateProject.isPending` is shared across every
  // card, so track the specific projects being duplicated. A Set (not a single
  // id) lets the user duplicate several different cards concurrently while each
  // card's own button stays disabled and its re-entrant clicks are dropped.
  const [duplicatingIds, setDuplicatingIds] = useState<Set<string>>(new Set())
  const duplicateProject = useDuplicateProject()
  const createProject = useCreateProject()
  const starProject = useStarProject()
  const unstarProject = useUnstarProject()
  const updateProject = useUpdateProject()
  // T284464051: keyed by project id (same shape as `duplicatingIds`) so one
  // card's in-flight status write does not disable every other card's control.
  const [statusUpdatingIds, setStatusUpdatingIds] = useState<Set<string>>(new Set())
  const navigate = useNavigate()
  const { user } = useAuth()
  const { t, i18n } = useTranslation('pages')
  const canRunWorkflow = canRunDeveloperWorkflow(user)
  const [workflowRunError, setWorkflowRunError] = useState<string | null>(null)

  const { data: tabsData, isLoading: isTabsLoading } = useDashboardTabs()
  const tabs = useMemo(() => tabsData?.tabs ?? [], [tabsData])
  const activeTab = activeTabId ? tabs.find((t) => t.id === activeTabId) : null

  // Named tabs ignore the sort query params server-side (they use their
  // persisted sort). Only pass sort hints when on the All tab.
  const queriedSortBy = activeTabId === null && allSortBy !== 'manual' ? allSortBy : undefined
  const queriedSortDirection = activeTabId === null ? allSortDirection : undefined
  const {
    data: pageData,
    isLoading: isPageLoading,
    // `isPending` rather than `isLoading` for the skeleton: the projects query
    // is held disabled until the flags snapshot and active team settle (so the
    // dashboard issues ONE request, not one per settlement), and while a query
    // is disabled react-query reports isLoading=false — it is `isPending &&
    // isFetching`, and nothing is in flight. Keying the skeleton off isLoading
    // would therefore render the empty state during that window: "no projects
    // yet" to someone who has plenty.
    isPending: isPagePending,
    isFetching: isPageFetching,
    isPlaceholderData,
    error,
  } = useDashboardTabProjects(
    tabIdParam,
    page,
    queriedSortBy,
    queriedSortDirection,
    namePrefix,
    visibility,
  )

  useEffect(() => {
    if (!error) return
    dashboardAvailabilityLog({
      event: 'backend-unavailable-placeholder-rendered',
      trace_id: 'dashboard-backend-unavailable-placeholder',
      route: 'landing',
      error_type: error instanceof Error ? error.name : typeof error,
      placeholder_variant: 'generic-unavailable',
      retry_action: 'reload',
    })
  }, [error])

  // RFC-1371 D3. One-way: publishProjectToTeam has no inverse route, so the
  // card button only OPENS a dialog and the dialog performs the move. The
  // confirmation carries the "cannot be undone" sentence, which is the part a
  // guarded button could not say.
  const publishToTeam = usePublishProjectToTeam()
  const [moveToTeamConfirm, setMoveToTeamConfirm] = useState<{
    id: string
    name: string
  } | null>(null)
  const [movingToTeamId, setMovingToTeamId] = useState<string | null>(null)

  const handleMoveToTeam = useCallback(async () => {
    if (!moveToTeamConfirm) return
    const projectId = moveToTeamConfirm.id
    setMovingToTeamId(projectId)
    try {
      await publishToTeam.mutateAsync(projectId)
      trackInteraction('teams_project_shared', { project_id: projectId, entry_point: 'dashboard', result: 'success' })
      // Close only on success. Leaving the dialog open on failure keeps the
      // user where the retry is, instead of dropping them back to a list that
      // looks unchanged with no explanation of which action failed.
      setMoveToTeamConfirm(null)
      toast.success(t('home.moveToTeamSuccess'))
    } catch {
      trackInteraction('teams_project_shared', { project_id: projectId, entry_point: 'dashboard', result: resultOf(false) })
      toast.error(t('home.moveToTeamError'))
    } finally {
      setMovingToTeamId(null)
    }
  }, [moveToTeamConfirm, publishToTeam, t])

  const handleVisibilityChange = useCallback(
    (next: ProjectVisibility) => {
      const params = new URLSearchParams(searchParams)
      if (next === DEFAULT_VISIBILITY) {
        // Keep the default OUT of the URL so the common case stays clean and
        // pre-teams links keep working unchanged.
        params.delete('visibility')
      } else {
        params.set('visibility', next)
      }
      // Page 1: the switch changes WHICH projects are being paged, so holding
      // the old offset would land on a page that may not exist in the new half.
      params.delete('page')
      setSearchParams(params)
    },
    [searchParams, setSearchParams],
  )

  // Role in the ACTIVE team. Undefined outside a team, where the question does
  // not arise. The server enforces this regardless (DELETE /projects/{id}
  // requires `admin`); this only decides whether to draw a button that would 403.
  // `personalTeam` is read for the empty-state CTA, which has to tell "this
  // account is new" apart from "this team is new" — see handleStartFirstProject.
  const { activeTeam, personalTeam } = useActiveTeam()
  const canDeleteThisTeamProject =
    activeTeam !== null &&
    canDeleteTeamProject({ role: activeTeam.myRole, kind: activeTeam.kind })

  const assignProject = useAssignProject()
  const unassignProject = useUnassignProject()
  const reorderTabs = useReorderTabs()
  const reorderProjects = useReorderProjects()

  // Track what kind of item is being dragged
  const [dragActiveType, setDragActiveType] = useState<'tab' | 'project' | null>(null)
  const [dragActiveId, setDragActiveId] = useState<string | null>(null)
  // Local reorder state — set synchronously in handleDragEnd to avoid flash
  // between dnd-kit transform reset and async cache update.
  const [pendingOrder, setPendingOrder] = useState<ProjectListItem[] | null>(null)
  // Timer for opening a tab on drag-over
  const dragOverTabTimer = useRef<ReturnType<typeof setTimeout>>()
  // Preserve the source tab for the full drag; hover-open can change activeTabId mid-drag.
  const dragSourceTabId = useRef<string | null>(null)
  const activeTabIdRef = useRef<string | null>(null)
  const dragOverTabIdRef = useRef<string | null | undefined>(undefined)
  const dragOpenedTabIdRef = useRef<string | null | undefined>(undefined)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 12 } }),
  )

  const handleDelete = async () => {
    if (!deleteConfirm) return
    try {
      await deleteProject.mutateAsync(deleteConfirm.id)
      setDeleteConfirm(null)
    } catch (err) {
      console.error('Failed to delete project:', err)
    }
  }

  const handleStatusChange = useCallback(
    async (projectId: string, status: ProjectStatus, currentStatus: ProjectStatus) => {
      statusLog({
        event: 'project-status-lifecycle:manual_override_requested',
        origin: 'frontend',
        trigger: 'manual',
        surface: 'dashboard',
        project_id: projectId,
        old_status: currentStatus,
        new_status: status,
      })
      setStatusUpdatingIds((prev) => new Set(prev).add(projectId))
      try {
        await updateProject.mutateAsync({ projectId, data: { status } })
        statusLog({
          event: 'project-status-lifecycle:manual_override_applied',
          origin: 'frontend',
          trigger: 'manual',
          surface: 'dashboard',
          project_id: projectId,
          old_status: currentStatus,
          new_status: status,
        })
      } catch (err) {
        // The hook already rolled the optimistic cache patch back; surface it
        // so the badge silently snapping back is not the only feedback.
        statusLog({
          event: 'project-status-lifecycle:manual_override_failed',
          origin: 'frontend',
          trigger: 'manual',
          surface: 'dashboard',
          project_id: projectId,
          old_status: currentStatus,
          new_status: status,
          error: err instanceof Error ? err.message : String(err),
        })
        toast.error(t('home.statusUpdateFailed'))
      } finally {
        setStatusUpdatingIds((prev) => {
          const next = new Set(prev)
          next.delete(projectId)
          return next
        })
      }
    },
    [updateProject, t],
  )

  const handleDuplicate = async (projectId: string) => {
    // Per-card re-entrancy guard — a slow clone on one card must NOT drop
    // clicks on other cards (each card owns its own in-flight state).
    if (duplicatingIds.has(projectId)) return
    duplicateProjectLog({ event: 'duplicate-click', source: 'dashboard', project_id: projectId })
    setDuplicatingIds((prev) => new Set(prev).add(projectId))
    try {
      const clone = await duplicateProject.mutateAsync(projectId)
      // When duplicating from a NAMED tab, assign the copy to that same tab so
      // it stays visible in the current view. Named-tab lists only return
      // assigned project_ids, so without this the clone would appear only under
      // the "All" tab and look like it vanished from where the user clicked.
      // Track whether that assignment actually succeeded so we don't claim a
      // plain success when the copy really only landed under "All".
      let assignedToActiveTab = true
      if (activeTabId) {
        try {
          await assignProject.mutateAsync({ tabId: activeTabId, projectId: clone.id })
        } catch (assignErr) {
          assignedToActiveTab = false
          duplicateProjectLog({
            event: 'duplicate-assign-error',
            clone_project_id: clone.id,
            tab_id: activeTabId,
            error: assignErr instanceof Error ? assignErr.message : String(assignErr),
          })
        }
      }
      // No auto-navigation: where the clone lands depends on server ordering we
      // can't reliably predict client-side — the All tab sorts starred-first
      // then by the chosen field, sort can be name/created/updated asc·desc, and
      // named tabs support manual order (assignment appends to the end). Any
      // "jump to page N" heuristic is wrong for some combination and can even
      // yank the view back if the user changed tab/page while the request was in
      // flight. The success toast is the reliable confirmation; the new card
      // appears in the list per the active sort (as with any paginated CRUD).
      duplicateProjectLog({
        event: 'duplicate-created',
        source_project_id: projectId,
        clone_project_id: clone.id,
        tab_id: activeTabId ?? 'all',
        assigned_to_active_tab: assignedToActiveTab,
      })
      // Honest feedback: if the tab assignment failed, tell the user the copy is
      // under "All" instead of implying it's in the current (named) tab.
      toast.success(
        assignedToActiveTab ? t('home.duplicateSuccess') : t('home.duplicateSuccessInAll'),
      )
    } catch (err) {
      const message = err instanceof Error ? err.message : t('home.duplicateFailed')
      duplicateProjectLog({ event: 'duplicate-error', project_id: projectId, error: message })
      toast.error(t('home.duplicateFailed'))
    } finally {
      setDuplicatingIds((prev) => {
        const next = new Set(prev)
        next.delete(projectId)
        return next
      })
    }
  }

  const [createProjectError, setCreateProjectError] = useState<string | null>(null)

  const trackProjectCreateClicked = (entryPoint: 'dashboard_header' | 'empty_tab' | 'first_project') => {
    const ctx = teamContextFor(activeTeam)
    trackInteraction('project_create_clicked', {
      entry_point: entryPoint,
      project_creation_intent_id: newAnalyticsId(),
      creation_source: 'project_page_new',
      owner_type: ctx.owner_type,
      team_id: ctx.team_id,
    })
  }

  const handleCreateProject = async () => {
    setCreateProjectError(null)
    newProjectLog({ event: 'new-project-click', source: 'dashboard' })
    try {
      const project = await createProject.mutateAsync({
        // Send the UI locale so the backend can localize the default project
        // name via its Axis-B catalog (#2340). Locale MUST arrive as an
        // explicit request param — the backend never reads it from JWT
        // user_metadata (S1-1). Mirrors conversation_language (#2105).
        ui_locale: normalizeLocale(i18n.language) ?? 'en',
        style: DEFAULT_STYLE,
      })
      newProjectLog({ event: 'new-project-created', project_id: project.id })
      navigate(`/project/${project.id}/workspace`)
    } catch (err) {
      const message = err instanceof Error ? err.message : t('home.failedToCreateProject')
      newProjectLog({ event: 'new-project-error', error: message })
      setCreateProjectError(message)
    }
  }

  const handleRunDeveloperWorkflow = async () => {
    setWorkflowRunError(null)
    try {
      const tag = new Date().toISOString().replace(/[-:.TZ]/g, '').slice(0, 12)
      const project = await createProject.mutateAsync({
        name: `Replay E2E ${tag}`,
        prompt: DEV_REPLAY_WORKFLOW_PROMPT,
        production_target: 'video',
        style: DEFAULT_STYLE,
        aspect_ratio: '16:9',
      })
      navigate(`/project/${project.id}/workspace`, {
        state: {
          initialPrompt: DEV_REPLAY_WORKFLOW_PROMPT,
          initialAutoConfirmCostSettings: { image: true, video: true },
          initialReplayScenario: DEV_REPLAY_WORKFLOW_SCENARIO,
        },
      })
    } catch (err) {
      setWorkflowRunError(err instanceof Error ? err.message : 'Failed to start workflow')
    }
  }

  const developerWorkflowButton = canRunWorkflow ? (
    <Button
      onClick={handleRunDeveloperWorkflow}
      size="sm"
      variant="secondary"
      loading={createProject.isPending}
      data-testid="run-smoke-workflow"
      title="Run the deterministic replay E2E workflow"
    >
      <Play className="h-3.5 w-3.5" />
      {t('home.replayE2E')}
    </Button>
  ) : null

  // Page coming from the server (already filtered + sorted + paginated).
  // Memoize so the reference is stable across renders when the underlying
  // data hasn't changed — pendingOrder's useEffect depends on it.
  const pageProjects = useMemo(() => pageData?.data ?? [], [pageData?.data])
  const total = pageData?.pagination.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / DASHBOARD_PAGE_SIZE))

  const handleStartFirstProject = () => {
    // `total` is SCOPED — by active team and by the Private/Team switch — so an
    // empty list is not by itself an onboarding signal. A stale/out-of-range
    // page and a freshly created team both land here with cards missing while
    // the account is not new; both keep quick-create.
    if (
      !shouldRedirectToGuidedCreation({
        scopedTotal: total,
        activeTeamId: activeTeam?.id ?? null,
        personalTeamId: personalTeam?.id ?? null,
        visibility,
      })
    ) {
      void handleCreateProject()
      return
    }

    const traceId = createTraceId()
    newProjectLog({
      event: 'first-project-guided-creation-redirect-requested',
      trace_id: traceId,
      trigger: 'dashboard-empty-state-new-project',
      scenario: 'zero-project-onboarding',
      routePath: '/dashboard -> /',
      target_type: 'guided-creation-homepage',
      from_route: '/dashboard',
      to_route: '/',
      project_count: total,
      is_first_project: total === 0,
      // Which scope the zero came from. Without these, a log line saying
      // "0 projects, redirected" cannot be told apart from the team-scoped
      // false positive this branch now excludes.
      scope_team_id: activeTeam?.id ?? null,
      scope_is_personal_team: activeTeam !== null && activeTeam.id === personalTeam?.id,
      scope_visibility: visibility,
      outcome: 'navigation-requested',
    })
    navigate('/')
  }

  // pendingOrder bridges the optimistic visual state during reorder so
  // dnd-kit doesn't flash. Clear it once the server cache catches up.
  useEffect(() => {
    setPendingOrder(null)
  }, [pageProjects])

  useEffect(() => {
    activeTabIdRef.current = activeTabId
  }, [activeTabId])

  // Reset to last page if the current page falls off the end (e.g. deletion
  // shrinks the result set). Avoids landing on a permanently-empty page.
  useEffect(() => {
    if (!isPageLoading && page > totalPages && total > 0) {
      const next = new URLSearchParams(searchParams)
      next.set('page', String(totalPages))
      setSearchParams(next, { replace: true })
    }
  }, [page, totalPages, total, isPageLoading, searchParams, setSearchParams])

  const switchTab = useCallback(
    (tabId: string | null) => {
      activeTabIdRef.current = tabId
      setPendingOrder(null)
      const next = new URLSearchParams(searchParams)
      next.set('tab', tabId ?? ALL_TAB_ID)
      next.set('page', '1')
      setSearchParams(next, { replace: false })
    },
    [searchParams, setSearchParams],
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      setInputValue(value)
      // PII guard: only the length + tab id are logged, never the literal
      // query text. Mirrors backend `name_prefix=<...>` debug field.
      dashboardSearchLog('input-changed length=%d tab=%s', value.length, tabIdParam)
    },
    [tabIdParam],
  )

  // 500ms debounce: write inputValue back to the URL ?q= param. The URL
  // doubles as the React Query key, so the refetch happens automatically.
  // Page resets to 1 when the query changes (filtered set may be shorter).
  useEffect(() => {
    if (inputValue === urlQuery) return
    const handle = setTimeout(() => {
      const next = new URLSearchParams(searchParams)
      const trimmed = inputValue.trim()
      if (trimmed) {
        next.set('q', inputValue)
      } else {
        next.delete('q')
      }
      next.set('page', '1')
      setSearchParams(next, { replace: true })
      dashboardSearchLog(
        'debounce-fired length=%d tab=%s',
        trimmed.length,
        tabIdParam,
      )
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [inputValue, urlQuery, searchParams, setSearchParams, tabIdParam])

  const goToPage = useCallback(
    (nextPage: number) => {
      const next = new URLSearchParams(searchParams)
      next.set('page', String(nextPage))
      setSearchParams(next, { replace: false })
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    [searchParams, setSearchParams],
  )

  // The list to render — pendingOrder prevents flash on reorder
  const displayProjects = pendingOrder ?? pageProjects
  const { setNodeRef: setProjectListDropRef } = useDroppable({
    id: activeTabId ? `project-list-${activeTabId}` : 'project-list-all',
    data: { type: 'project-list', tabId: activeTabId },
  })

  // Sortable IDs for the project list
  const sortableProjectIds = useMemo(
    () => displayProjects.map((p) => `project-${p.id}`),
    [displayProjects],
  )

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const data = event.active.data.current
      if (data?.type === 'tab') {
        dragSourceTabId.current = null
        dragOverTabIdRef.current = undefined
        dragOpenedTabIdRef.current = undefined
        setDragActiveType('tab')
        setDragActiveId(String(event.active.id))
      } else if (data?.type === 'project') {
        dragSourceTabId.current = activeTabId
        dragOverTabIdRef.current = activeTabId
        dragOpenedTabIdRef.current = activeTabId
        setDragActiveType('project')
        setDragActiveId(typeof data.projectId === 'string' ? data.projectId : null)
      }
    },
    [activeTabId],
  )

  const handleProjectHoverTab = useCallback(
    (tabId: string | null | undefined) => {
      if (dragActiveType !== 'project') return

      dragOverTabIdRef.current = tabId
      clearTimeout(dragOverTabTimer.current)

      if (tabId === undefined || tabId === activeTabIdRef.current) return

      dragOverTabTimer.current = setTimeout(() => {
        dragOpenedTabIdRef.current = tabId
        switchTab(tabId)
      }, 400)
    },
    [dragActiveType, switchTab],
  )

  useEffect(() => {
    if (dragActiveType !== 'project') return

    const handleHoverMove = (event: MouseEvent | PointerEvent) => {
      const hoverEl = Array.from(document.querySelectorAll<HTMLElement>('[data-project-hover-tab-id]')).find((el) => {
        const rect = el.getBoundingClientRect()
        return event.clientX >= rect.left
          && event.clientX <= rect.right
          && event.clientY >= rect.top
          && event.clientY <= rect.bottom
      }) ?? null
      const rawTabId = hoverEl?.dataset.projectHoverTabId
      const tabId = rawTabId === '__all__'
        ? null
        : rawTabId ?? undefined
      handleProjectHoverTab(tabId)
    }

    window.addEventListener('pointermove', handleHoverMove, true)
    window.addEventListener('mousemove', handleHoverMove, true)
    return () => {
      window.removeEventListener('pointermove', handleHoverMove, true)
      window.removeEventListener('mousemove', handleHoverMove, true)
    }
  }, [dragActiveType, handleProjectHoverTab])

  const handleDragCancel = useCallback(() => {
    setDragActiveType(null)
    setDragActiveId(null)
    dragSourceTabId.current = null
    dragOverTabIdRef.current = undefined
    dragOpenedTabIdRef.current = undefined
    clearTimeout(dragOverTabTimer.current)
  }, [])

  const pageOffset = (page - 1) * DASHBOARD_PAGE_SIZE

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      setDragActiveType(null)
      setDragActiveId(null)
      const sourceTabId = dragSourceTabId.current
      const hoveredTabId = dragOverTabIdRef.current
      const openedTabId = dragOpenedTabIdRef.current
      dragSourceTabId.current = null
      dragOverTabIdRef.current = undefined
      dragOpenedTabIdRef.current = undefined
      clearTimeout(dragOverTabTimer.current)
      const activeData = active.data.current
      const currentActiveTabId = activeTabIdRef.current
      const moveProjectToTab = (projectId: string, targetTabId: string | null) => {
        if (targetTabId === sourceTabId) return

        if (targetTabId === null) {
          if (sourceTabId) {
            dashboardLog('project unassign: %s from tab %s', projectId, sourceTabId)
            unassignProject.mutate({ tabId: sourceTabId, projectId })
          }
          switchTab(null)
          return
        }

        dashboardLog('project assign: %s to tab %s', projectId, targetTabId)
        assignProject.mutate({ tabId: targetTabId, projectId })
        switchTab(targetTabId)
      }

      if (!over) {
        if (activeData?.type === 'project' && sourceTabId !== currentActiveTabId && openedTabId === currentActiveTabId) {
          if (typeof activeData.projectId === 'string') {
            moveProjectToTab(activeData.projectId, currentActiveTabId)
          }
        }
        return
      }

      const overData = over.data.current
      const overId = String(over.id)

      if (activeData?.type === 'project') {
        const targetTabId = overId === 'project-hover-all'
          ? null
          : overId.startsWith('project-hover-tab-')
            ? overId.slice('project-hover-tab-'.length)
            : undefined

        if (targetTabId !== undefined) {
          if (typeof activeData.projectId === 'string') {
            moveProjectToTab(activeData.projectId, targetTabId)
          }
          return
        }

        if (sourceTabId !== currentActiveTabId && openedTabId === currentActiveTabId) {
          if (typeof activeData.projectId === 'string') {
            moveProjectToTab(activeData.projectId, currentActiveTabId)
          }
          return
        }

        if (sourceTabId !== currentActiveTabId && (overData?.type === 'project' || overData?.type === 'project-list')) {
          if (typeof activeData.projectId === 'string') {
            moveProjectToTab(activeData.projectId, hoveredTabId ?? currentActiveTabId)
          }
          return
        }
      }

      // ── Tab + Tab → tab reorder ──
      if (activeData?.type === 'tab' && overData?.type === 'tab') {
        if (active.id === over.id) return
        const oldIndex = tabs.findIndex((t) => t.id === active.id)
        const newIndex = tabs.findIndex((t) => t.id === over.id)
        if (oldIndex === -1 || newIndex === -1) return

        const newOrder = arrayMove(
          tabs.map((t) => t.id),
          oldIndex,
          newIndex,
        )
        dashboardLog('tab reorder: %d -> %d', oldIndex, newIndex)
        reorderTabs.mutate(newOrder)
        return
      }

      // ── Project + Project → project reorder within the visible page ──
      if (activeData?.type === 'project' && overData?.type === 'project') {
        if (active.id === over.id) return

        const activeProjectId = typeof activeData.projectId === 'string' ? activeData.projectId : undefined
        const overProjectId = typeof overData.projectId === 'string' ? overData.projectId : undefined
        if (!activeProjectId || !overProjectId) return

        // When hover-open switches tabs mid-drag, dropping over a project card
        // should move into that tab context, not attempt a reorder from the source tab.
        if (sourceTabId !== currentActiveTabId) {
          moveProjectToTab(activeProjectId, currentActiveTabId)
          return
        }

        // The All tab is server-fixed sort (starred + chosen sort) — there's
        // nowhere to persist a manual order. Tab-assignment drag still works
        // via the targetTabId branch above.
        if (!activeTab) {
          dashboardLog('project reorder skipped on All tab')
          reorderLog({
            event: 'reorder-blocked-all-tab',
            active_project_id: activeProjectId,
            over_project_id: overProjectId,
          })
          toast.error(
            t('home.reorderDisabledOnAllTab'),
          )
          return
        }

        // Manual sort is required for drag-to-reorder to have stable
        // splice semantics. When a named tab is still in updated_at / name
        // etc., the displayed order != stored project_ids order, so the
        // offset-based splice would hit a window-mismatch 400. Require the
        // user to switch to Manual Order first — the tab bar's sort
        // dropdown includes manual as an explicit choice.
        if (activeTab.sort_by !== 'manual') {
          dashboardLog('project reorder blocked: tab not in manual sort (sort_by=%s)', activeTab.sort_by)
          reorderLog({
            event: 'reorder-blocked-non-manual',
            tab_id: activeTab.id,
            sort_by: activeTab.sort_by,
            active_project_id: activeProjectId,
            over_project_id: overProjectId,
          })
          toast.error(
            t('home.reorderRequiresManualSort'),
          )
          return
        }

        const currentIds = displayProjects.map((p) => p.id)
        const oldIndex = currentIds.indexOf(activeProjectId)
        const newIndex = currentIds.indexOf(overProjectId)
        if (oldIndex === -1 || newIndex === -1) return

        const newOrder = arrayMove(currentIds, oldIndex, newIndex)
        const reordered = newOrder
          .map((id) => displayProjects.find((p) => p.id === id))
          .filter((p): p is ProjectListItem => p !== undefined)

        // Set local state synchronously to prevent flash
        setPendingOrder(reordered)

        dashboardLog(
          'project reorder: %d -> %d in tab %s (offset %d)',
          oldIndex,
          newIndex,
          activeTab.id,
          pageOffset,
        )
        reorderLog({
          event: 'reorder-attempt',
          tab_id: activeTab.id,
          old_index: oldIndex,
          new_index: newIndex,
          offset: pageOffset,
          active_project_id: activeProjectId,
          over_project_id: overProjectId,
        })
        reorderProjects.mutate(
          {
            tabId: activeTab.id,
            projectIds: newOrder,
            offset: pageOffset,
          },
          {
            onSuccess: () => {
              reorderLog({
                event: 'reorder-success',
                tab_id: activeTab.id,
                old_index: oldIndex,
                new_index: newIndex,
              })
            },
            onError: (err) => {
              const message = err instanceof Error ? err.message : String(err)
              reorderLog({
                event: 'reorder-error',
                tab_id: activeTab.id,
                error: message,
              })
              toast.error(t('home.reorderFailed'))
              // pendingOrder will be cleared when pageProjects refetches or
              // via the effect that watches pageProjects; ensure it doesn't
              // stay stuck on a failed optimistic state for long.
              setTimeout(() => setPendingOrder(null), 1000)
            },
          },
        )
        return
      }
    },
    [
      activeTab,
      tabs,
      assignProject,
      unassignProject,
      reorderTabs,
      reorderProjects,
      displayProjects,
      pageOffset,
      switchTab,
      t,
    ],
  )

  // Find the dragged project for the overlay
  const dragOverlayProject = dragActiveType === 'project' && dragActiveId
    ? pageProjects.find((p) => p.id === dragActiveId)
    : null

  // `isProjectListLoading` scopes the skeleton to the project-list region
  // only — the search input + tab bar must stay mounted across query refetches
  // so the input never loses focus mid-type. With `placeholderData:
  // keepPreviousData` on the hook, this branch fires only on the FIRST load
  // for a given (tab, sort, prefix) tuple where no previous result exists.
  // `isPagePending` covers both "in flight" and "held disabled until the team
  // scope settles"; `isLoading` would be false in the second case and drop the
  // user into the empty state. `!pageData` is kept so a placeholder-backed
  // refetch is not treated as a cold load.
  // Which visibility the rows CURRENTLY on screen belong to. Updated only when
  // real (non-placeholder) data settles, so it lags the switch by exactly the
  // request it is waiting on.
  const renderedVisibilityRef = useRef<ProjectVisibility>(visibility)
  useEffect(() => {
    if (!isPlaceholderData && pageData) renderedVisibilityRef.current = visibility
  }, [isPlaceholderData, pageData, visibility])

  const isProjectListLoading = computeProjectListLoading({
    isTabsLoading,
    isPagePending,
    hasPageData: Boolean(pageData),
    isPlaceholderData,
    renderedVisibility: renderedVisibilityRef.current,
    selectedVisibility: visibility,
  })
  // `isRefetchingList` fires only while the user is staring at PLACEHOLDER
  // data (the previous result, served via `placeholderData: keepPreviousData`)
  // and still waiting for the actual response for the current query key.
  // We deliberately do NOT use `isFetching` alone — that flips true on every
  // stale-time-driven background revalidation too, which would leave the
  // spinner up for seconds after the list visually settled (the cached entry
  // is already correct on screen; the background refresh is irrelevant to
  // the user). Spinner = "you're waiting for the right answer", not
  // "any network is in flight".
  const isRefetchingList = isPageFetching && isPlaceholderData && !isProjectListLoading

  if (error) {
    return (
      <div data-page="landing" className="relative min-h-screen">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="rounded-xl border border-border-subtle bg-surface-0 p-8 text-center shadow-2xl animate-fade-in">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-surface-1 text-muted-foreground">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-foreground">{t('home.unavailableTitle', { appName: APP_NAME })}</h2>
            <p className="mt-3 text-base text-muted-foreground">
              {t('home.unavailableDescription')}
            </p>
            <div className="mt-6 flex justify-center">
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
              >
                {t('home.tryAgain')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div data-page="landing" className="relative min-h-screen">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Delete Confirmation Modal */}
        {moveToTeamConfirm && (
          <MoveToTeamDialog
            projectName={moveToTeamConfirm.name}
            isMoving={publishToTeam.isPending}
            onCancel={() => setMoveToTeamConfirm(null)}
            onConfirm={handleMoveToTeam}
          />
        )}

        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fade-in">
            <div className="bg-panel rounded-lg p-6 max-w-md mx-4 shadow-2xl border border-border animate-scale-in">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-red-500/10 rounded-md border border-red-500/20">
                  <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{t('home.deleteConfirmTitle')}</h3>
                  <p className="text-text-secondary mt-2 text-sm">
                    {t('home.deleteConfirmMessage', { name: deleteConfirm.name })}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleteProject.isPending}
                >
                  {t('home.cancel')}
                </Button>
                <Button
                  variant="default"
                  className="!bg-red-600 hover:!bg-red-700 !shadow !shadow-red-600/20"
                  onClick={handleDelete}
                  loading={deleteProject.isPending}
                >
                  {t('home.delete')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Prefix-search input sits above the tab bar; same container width
            so it visually anchors to the dashboard's left edge. The
            Private/Team switch is its right-hand counterpart on the same row —
            search is SCOPED by it, so putting them on one line says they
            belong to the same query rather than being independent controls. */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <DashboardSearchInput value={inputValue} onChange={handleSearchChange} />
          </div>
          <div className="flex-shrink-0 pt-1">
            <VisibilitySwitch value={visibility} onChange={handleVisibilityChange} />
          </div>
        </div>

        {/* Single unified DndContext wrapping both tab bar and project list */}
        <DndContext
          sensors={sensors}
          collisionDetection={tabAwareCollision}
          onDragStart={handleDragStart}
          onDragCancel={handleDragCancel}
          onDragEnd={handleDragEnd}
        >
          {/* Tab Bar */}
          <DashboardTabBar
            tabs={tabs}
            activeTabId={activeTabId}
            onTabChange={switchTab}
            allSortBy={allSortBy}
            allSortDirection={allSortDirection}
            projectDragActive={dragActiveType === 'project'}
            onProjectHoverTab={handleProjectHoverTab}
            onAllSort={(sb, sd) => {
              setAllSortBy(sb)
              setAllSortDirection(sd)
            }}
          />
          {workflowRunError && (
            <div
              className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-foreground"
              data-testid="run-smoke-workflow-error"
            >
              {workflowRunError}
            </div>
          )}
          {createProjectError && (
            <div
              className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-foreground"
              data-testid="create-project-error"
            >
              {createProjectError}
            </div>
          )}

          {/* User Projects with sortable context for reorder */}
          <div ref={setProjectListDropRef}>
            {isProjectListLoading && (
              <div className="space-y-4" data-testid="dashboard-project-list-skeleton">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="skeleton h-28 rounded-md bg-surface-1" />
                ))}
              </div>
            )}
            {!isProjectListLoading && (
            <>
            <SortableContext items={sortableProjectIds} strategy={verticalListSortingStrategy}>
              {displayProjects.length > 0 && (
                <section>
                  <div className="flex items-center gap-3 mb-5">
                    <h2 className="text-lg font-semibold text-white">
                      {activeTab ? activeTab.name : t('home.yourProjects')}
                    </h2>
                    <span
                      className="px-2.5 py-0.5 text-xs font-medium text-text-secondary bg-surface-2 border border-white/10 rounded-full"
                      data-testid="dashboard-total-count"
                    >
                      {total}
                    </span>
                    {isRefetchingList && (
                      <svg
                        className="w-4 h-4 text-text-muted animate-spin"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-label={t('home.refreshingProjects')}
                        role="status"
                        data-testid="dashboard-list-refetch-indicator"
                      >
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                        <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      </svg>
                    )}
                    <Button
                      onClick={() => { trackProjectCreateClicked('dashboard_header'); void handleCreateProject() }}
                      size="sm"
                      className="ml-auto"
                      loading={createProject.isPending}
                      data-testid="create-project-header"
                    >
                      {t('home.newProject')}
                    </Button>
                    {developerWorkflowButton}
                  </div>
                  <div className="space-y-3">
                    {displayProjects.map((project) => (
                      <SortableProjectCard
                        key={project.id}
                        project={project}
                        dragDisabled={isSearching}
                        onDeleteClick={
                          // A project PUBLISHED to a team may only be deleted
                          // by an Owner or Admin -- not a member, and not its
                          // creator. Private projects keep the pre-teams
                          // behaviour (yours to delete), which is why this
                          // narrows on `visibility === 'team'` rather than on
                          // being in a team at all.
                          project.visibility === 'team' && !canDeleteThisTeamProject
                            ? undefined
                            : () => setDeleteConfirm({ id: project.id, name: project.name })
                        }
                        onDuplicateClick={() => handleDuplicate(project.id)}
                        isDuplicating={duplicatingIds.has(project.id)}
                        onMoveToTeamClick={() =>
                          setMoveToTeamConfirm({ id: project.id, name: project.name })
                        }
                        isMovingToTeam={movingToTeamId === project.id}
                        onStarClick={() => {
                          if (project.starred_at) {
                            unstarProject.mutate(project.id)
                          } else {
                            starProject.mutate(project.id)
                          }
                        }}
                        onStatusChange={(status) => handleStatusChange(project.id, status, project.status)}
                        isUpdatingStatus={statusUpdatingIds.has(project.id)}
                      />
                    ))}
                  </div>
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={goToPage}
                  />
                </section>
              )}
            </SortableContext>

            {/* Empty state */}
            {displayProjects.length === 0 && (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-panel border border-border mb-6">
                  <svg className="w-8 h-8 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                {isSearching ? (
                  <>
                    <h3
                      className="text-lg font-semibold text-white mb-2"
                      data-testid="dashboard-search-empty"
                    >
                      {t('home.noSearchResults', { query: urlQuery })}
                    </h3>
                    <div className="flex justify-center gap-2 mt-6">
                      <Button
                        onClick={() => {
                          setInputValue('')
                          const next = new URLSearchParams(searchParams)
                          next.delete('q')
                          next.set('page', '1')
                          setSearchParams(next, { replace: true })
                        }}
                        size="sm"
                        variant="secondary"
                        data-testid="dashboard-search-empty-clear"
                      >
                        {t('home.clearSearch')}
                      </Button>
                    </div>
                  </>
                ) : activeTab ? (
                  <>
                    <h3 className="text-lg font-semibold text-white mb-2">{t('home.noProjectsInTab')}</h3>
                    <p className="text-text-secondary text-sm mb-6">{t('home.dragToOrganize')}</p>
                    <div className="flex justify-center gap-2">
                      <Button
                        onClick={() => { trackProjectCreateClicked('empty_tab'); void handleCreateProject() }}
                        size="sm"
                        variant="secondary"
                        loading={createProject.isPending}
                        data-testid="create-project-empty-tab"
                      >
                        {t('home.newProject')}
                      </Button>
                      {developerWorkflowButton}
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="text-xl font-semibold text-white mb-2">{t('home.createFirstProject')}</h3>
                    <p className="text-text-secondary text-sm mb-6">{t('home.createFirstProjectDescription', { appName: APP_NAME })}</p>
                    <div className="flex justify-center gap-2">
                      <Button
                        onClick={() => { trackProjectCreateClicked('first_project'); handleStartFirstProject() }}
                        loading={createProject.isPending}
                        data-testid="create-project-hero"
                      >
                        {t('home.newProject')}
                      </Button>
                      {developerWorkflowButton}
                    </div>
                  </>
                )}
              </div>
            )}
            </>
            )}
          </div>

          {/* Drag Overlay */}
          <DragOverlay dropAnimation={null}>
            {dragActiveType === 'project' && dragOverlayProject && (
              <div className="opacity-80 shadow-2xl" style={{ width: '100%', maxWidth: '800px' }}>
                <ProjectCardContent project={dragOverlayProject} />
              </div>
            )}
            {dragActiveType === 'tab' && dragActiveId && (() => {
              const tab = tabs.find((t) => t.id === dragActiveId)
              if (!tab) return null
              return (
                <div className="px-3 py-1.5 rounded-t-md text-sm bg-surface-1 text-text-secondary opacity-80 shadow-xl border border-border">
                  {tab.name}
                </div>
              )
            })()}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  )
}
